import { getBackendUrl } from '@/lib/backendUrl';
import { getApiError } from '@/lib/utils';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Link2, Link2Off, Plus, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = getBackendUrl();

const BLOCKING_LABELS = {
  no_site_on_job: 'No site recorded on this job.',
  no_auditors_assigned: 'No auditors are assigned to this job.',
  no_billable_lines: 'No billable service lines on this job.',
  missing_or_ambiguous_target: 'has no resolved QBO target.',
  not_yet_verified_in_qbo: 'is mapped but not yet verified active in QBO.',
  auditor_region_unassigned: 'has no QBO Region assigned yet — set it in Sales Invoicing > Settings.',
};

function authHeaders() {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function fmtDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB');
}

export default function LinkJobsPanel() {
  const [search, setSearch] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [suggestions, setSuggestions] = useState(null);
  const [review, setReview] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [links, setLinks] = useState([]);
  const searchDebounceRef = useRef(null);
  const requestIdRef = useRef(0);

  const loadCandidates = useCallback(async (term) => {
    const requestId = ++requestIdRef.current;
    setLoadingCandidates(true);
    try {
      const params = term ? { search: term } : {};
      const { data } = await axios.get(`${API}/api/sales-invoicing/link-jobs/candidates`, { params, headers: authHeaders() });
      if (requestId !== requestIdRef.current) return;  // a newer search has since superseded this one — never let a slower, stale response overwrite it
      setCandidates(data.candidates || []);
      setLoadError(null);
    } catch (e) {
      if (requestId !== requestIdRef.current) return;
      const message = getApiError(e, 'Failed to load completed jobs — check your connection and try again.');
      setLoadError(message);
      toast.error(message);
    } finally {
      if (requestId === requestIdRef.current) setLoadingCandidates(false);
    }
  }, []);

  const loadLinks = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/sales-invoicing/link-jobs`, { headers: authHeaders() });
      setLinks(data.links || []);
    } catch {
      // non-critical — the panel still works without the list below
    }
  }, []);

  useEffect(() => { loadLinks(); }, [loadLinks]);

  useEffect(() => {
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => loadCandidates(search), 350);
    return () => clearTimeout(searchDebounceRef.current);
  }, [search, loadCandidates]);

  useEffect(() => {
    if (selectedIds.length === 0) { setSuggestions(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/sales-invoicing/link-jobs/suggestions`, {
          params: { job_ids: selectedIds.join(',') }, headers: authHeaders(),
        });
        if (!cancelled) setSuggestions((data.suggested_jobs || []).length > 0 ? data : null);
      } catch {
        if (!cancelled) setSuggestions(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedIds]);

  const toggle = (jobId, isLinked, isExported) => {
    if (isLinked || isExported) return;
    setSelectedIds((prev) => (prev.includes(jobId) ? prev.filter((x) => x !== jobId) : [...prev, jobId]));
    setReview(null);
  };

  const addSuggested = (jobId) => {
    setSelectedIds((prev) => (prev.includes(jobId) ? prev : [...prev, jobId]));
    setReview(null);
  };

  const doReview = async () => {
    setReviewing(true);
    setReview(null);
    try {
      const { data } = await axios.post(`${API}/api/sales-invoicing/link-jobs/review`, { job_ids: selectedIds }, { headers: authHeaders() });
      setReview(data);
      setDueDate(data.suggested_due_date || '');
    } catch (e) {
      toast.error(getApiError(e, 'Failed to review the selected jobs.'));
    } finally {
      setReviewing(false);
    }
  };

  const doConfirm = async () => {
    const num = parseInt(invoiceNumber, 10);
    if (!num || num < 1) { toast.error('Enter the one QBO invoice number for this group.'); return; }
    if (review?.due_days_conflict && !dueDate) { toast.error('These sites use different due-day rules — choose the due date.'); return; }
    setConfirming(true);
    try {
      const body = { job_ids: selectedIds, invoice_number: num };
      if (dueDate) body.due_date = dueDate;
      const { data } = await axios.post(`${API}/api/sales-invoicing/link-jobs/confirm`, body, { headers: authHeaders() });
      toast.success(`Linked ${selectedIds.length} jobs under invoice ${data.invoice_number}.`);
      setSelectedIds([]); setReview(null); setInvoiceNumber(''); setDueDate('');
      loadCandidates(search); loadLinks();
    } catch (e) {
      toast.error(getApiError(e, 'Failed to confirm the link — recheck the review above.'));
    } finally {
      setConfirming(false);
    }
  };

  const unlink = async (linkId) => {
    try {
      await axios.delete(`${API}/api/sales-invoicing/link-jobs/${linkId}`, { headers: authHeaders() });
      toast.success('Unlinked.');
      loadLinks(); loadCandidates(search);
    } catch (e) {
      toast.error(getApiError(e, 'Failed to unlink — it may already be exported.'));
    }
  };

  return (
    <div className="space-y-6" data-testid="link-jobs-panel">
      <div>
        <h3 className="text-lg font-semibold">Link Jobs</h3>
        <p className="text-sm text-slate-500">Pick 2 or more completed jobs and review them under one QBO invoice number. Each job keeps its own service, class, region, VAT and Cellar Ledger lines — only the invoice number and date are shared.</p>
      </div>

      <div className="flex items-center gap-3">
        <Input
          placeholder="Search by site name or job reference…" value={search} onChange={(e) => setSearch(e.target.value)}
          className="w-80" data-testid="link-jobs-search-input"
        />
        {selectedIds.length > 0 && (
          <Badge variant="secondary" data-testid="link-jobs-selected-count">{selectedIds.length} selected</Badge>
        )}
        <Button size="sm" disabled={selectedIds.length < 2 || reviewing} onClick={doReview} data-testid="link-jobs-review-btn">
          {reviewing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Link2 className="h-4 w-4 mr-1" />}
          Review Link ({selectedIds.length})
        </Button>
      </div>

      {suggestions && (
        <div className="rounded-md border border-sky-200 bg-sky-50 text-sky-900 text-sm px-3 py-2 flex flex-wrap items-center gap-2" data-testid="link-jobs-suggestion-banner">
          <span>You've grouped these sites together before — also link:</span>
          {suggestions.suggested_jobs.map((sj) => (
            <Button key={sj.job_id} size="sm" variant="outline" className="h-7"
              onClick={() => addSuggested(sj.job_id)} data-testid={`link-jobs-add-suggested-${sj.job_id}`}>
              <Plus className="h-3 w-3 mr-1" />{sj.site_name} ({fmtDate(sj.scheduled_date)})
            </Button>
          ))}
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10"></TableHead>
            <TableHead>Site</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Job Reference</TableHead>
            <TableHead>Customer</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loadingCandidates && (
            <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-6">Loading…</TableCell></TableRow>
          )}
          {!loadingCandidates && loadError && (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-6" data-testid="link-jobs-load-error">
                <div className="flex flex-col items-center gap-2 text-red-600">
                  <span className="flex items-center gap-1"><AlertTriangle className="h-4 w-4" />{loadError}</span>
                  <Button size="sm" variant="outline" onClick={() => loadCandidates(search)} data-testid="link-jobs-retry-btn">Retry</Button>
                </div>
              </TableCell>
            </TableRow>
          )}
          {!loadingCandidates && !loadError && candidates.length === 0 && (
            <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-6">No completed jobs found.</TableCell></TableRow>
          )}
          {!loadError && candidates.map((c) => {
            const disabled = c.already_linked || c.already_exported;
            return (
              <TableRow key={c.job_id} className={disabled ? 'opacity-50' : ''} data-testid={`link-jobs-candidate-row-${c.job_id}`}>
                <TableCell>
                  <Checkbox
                    checked={selectedIds.includes(c.job_id)} disabled={disabled}
                    onCheckedChange={() => toggle(c.job_id, c.already_linked, c.already_exported)}
                    data-testid={`link-jobs-candidate-checkbox-${c.job_id}`}
                  />
                </TableCell>
                <TableCell>{c.hotel_name || '—'}</TableCell>
                <TableCell>{fmtDate(c.scheduled_date)}</TableCell>
                <TableCell className="font-mono text-xs">{c.job_reference}</TableCell>
                <TableCell>{c.customer_name || <span className="text-amber-600">unresolved</span>}</TableCell>
                <TableCell>
                  {c.already_exported && <Badge variant="outline" className="border-slate-400">Exported</Badge>}
                  {c.already_linked && !c.already_exported && (
                    <Badge variant="outline" className="border-sky-400 text-sky-700">Linked (#{c.linked_invoice_number})</Badge>
                  )}
                  {!c.already_exported && !c.already_linked && c.decision_needed && (
                    <Badge variant="outline" className="border-amber-400 text-amber-700">Decision needed</Badge>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {review && (
        <div className="rounded-lg border p-4 space-y-4" data-testid="link-jobs-review-panel">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <Label className="text-xs text-slate-500">QBO Invoice Number</Label>
              <Input
                type="number" min={1} value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)}
                className="w-40" data-testid="link-jobs-invoice-number-input"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Entry Date</Label>
              <div className="h-9 flex items-center text-sm" data-testid="link-jobs-entry-date">{fmtDate(review.entry_date)}</div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">
                Due Date {review.due_days_conflict && <span className="text-amber-600">— choice required, sites disagree ({review.distinct_due_days.join(' vs ')} days)</span>}
              </Label>
              <Input
                type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                className="w-48" data-testid="link-jobs-due-date-input"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Bill To</Label>
              <div className="h-9 flex items-center">
                {review.common_customer ? (
                  <Badge className="bg-emerald-600">{review.common_customer}</Badge>
                ) : (
                  <Badge variant="outline" className="border-amber-400 text-amber-700">Unresolved / conflicting</Badge>
                )}
              </div>
            </div>
          </div>

          {review.customer_conflict && (
            <div className="rounded-md border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2" data-testid="link-jobs-customer-conflict">
              These jobs resolve to different QBO customers ({review.distinct_customers.join(', ')}) — all linked jobs must share one approved bill-to.
            </div>
          )}
          {review.already_linked_job_ids.length > 0 && (
            <div className="rounded-md border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2" data-testid="link-jobs-already-linked-warning">
              Already linked elsewhere: job{review.already_linked_job_ids.length > 1 ? 's' : ''} {review.already_linked_job_ids.join(', ')}.
            </div>
          )}
          {review.already_exported_job_ids.length > 0 && (
            <div className="rounded-md border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2" data-testid="link-jobs-already-exported-warning">
              Already exported: job{review.already_exported_job_ids.length > 1 ? 's' : ''} {review.already_exported_job_ids.join(', ')} — an exported job can't be linked.
            </div>
          )}

          <div className="grid gap-3">
            {review.jobs.map((j) => (
              <div key={j.job_id} className="rounded-md border p-3 text-sm" data-testid={`link-jobs-job-card-${j.job_id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <div className="font-medium">{j.hotel_name} — {fmtDate(j.scheduled_date)} <span className="text-slate-400 font-mono text-xs">{j.job_reference}</span></div>
                  <div className="flex items-center gap-2">
                    {j.region ? <Badge variant="secondary">{j.region}</Badge> : <Badge variant="outline" className="border-amber-400 text-amber-700">Region unresolved</Badge>}
                    {j.is_ready ? <Badge className="bg-emerald-600"><CheckCircle2 className="h-3 w-3 mr-1" />Ready</Badge> : <Badge variant="outline" className="border-red-400 text-red-700">Not ready</Badge>}
                  </div>
                </div>
                <div className="space-y-1">
                  {j.service_lines.map((sl, i) => (
                    <div key={i} className="flex justify-between text-slate-600">
                      <span>{sl.service_name}{sl.is_cellar_ledger ? ' (Cellar Ledger)' : ''}</span>
                      <span>£{Number(sl.price).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between font-medium mt-1 pt-1 border-t">
                  <span>Net / VAT / Total</span>
                  <span>£{j.net.toFixed(2)} / £{j.vat.toFixed(2)} / £{j.total_inc_vat.toFixed(2)}</span>
                </div>
                {j.blocking_decisions.length > 0 && (
                  <ul className="mt-2 text-xs text-red-700 list-disc list-inside">
                    {j.blocking_decisions.map((b, i) => (
                      <li key={i}>{b.label} {BLOCKING_LABELS[b.reason] || b.reason.replace(/_/g, ' ')}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>

          <Button
            disabled={!review.can_confirm || confirming || (review.due_days_conflict && !dueDate)}
            onClick={doConfirm} data-testid="link-jobs-confirm-btn"
          >
            {confirming ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Link2 className="h-4 w-4 mr-1" />}
            Confirm Link
          </Button>
        </div>
      )}

      {links.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-slate-600 mb-2">Linked Groups</h4>
          <div className="space-y-2">
            {links.map((l) => (
              <div key={l.id} className="rounded-md border p-3 flex items-center justify-between flex-wrap gap-2 text-sm" data-testid={`link-jobs-group-${l.id}`}>
                <div>
                  <span className="font-medium">Invoice #{l.invoice_number}</span> — {l.customer_reference} — Due {fmtDate(l.due_date)}
                  <div className="text-slate-500 text-xs mt-1">{l.jobs.map((j) => `${j.site_name} (${fmtDate(j.scheduled_date)})`).join('  •  ')}</div>
                </div>
                <div className="flex items-center gap-2">
                  {l.any_exported ? (
                    <Badge variant="outline" className="border-slate-400">Exported</Badge>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => unlink(l.id)} data-testid={`link-jobs-unlink-btn-${l.id}`}>
                      <Link2Off className="h-3 w-3 mr-1" />Unlink
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!review && selectedIds.length > 0 && (
        <div className="text-xs text-amber-600 flex items-center gap-1" data-testid="link-jobs-select-hint">
          <AlertTriangle className="h-3 w-3" />Click "Review Link" to check these jobs before confirming.
        </div>
      )}
    </div>
  );
}
